/**
 * @typedef {Record<string, string>} VatMessages
 */

/**
 * @typedef {Object} VatValidationResponse
 * @property {boolean} [valid]
 * @property {boolean} [isValid]
 * @property {string} [error]
 * @property {string} [message]
 * @property {string} [error_message]
 */

/**
 * @typedef {Object} VatValidationResult
 * @property {boolean} valid
 * @property {string} [errorMessage]
 */

/**
 * Cart drawer VAT validation.
 *
 * Messages are loaded from theme customizer via data-vat-messages in liquid.
 */
class VatValidator extends HTMLElement {
  /** @type {boolean} */
  #isValidating = false;

  /** @type {boolean} */
  #initialized = false;

  /** @type {VatMessages | null} */
  #messages = null;

  /** @type {HTMLFormElement | null} */
  form = null;

  /** @type {HTMLInputElement | null} */
  vatInput = null;

  /** @type {HTMLInputElement | null} */
  emailInput = null;

  /** @type {HTMLButtonElement | null} */
  validateButton = null;

  /** @type {HTMLElement | null} */
  successMessage = null;

  /** @type {HTMLElement | null} */
  invalidMessage = null;

  /** @type {HTMLElement | null} */
  feedbackMessage = null;

  /** @type {NodeListOf<HTMLElement> | null} */
  spinnerIcons = null;

  /** @type {NodeListOf<HTMLElement> | null} */
  checkIcons = null;

  /** @type {HTMLInputElement | null} */
  vatValidatedField = null;

  /** @type {HTMLInputElement | null} */
  vatTimestampField = null;

  /** @type {HTMLInputElement | null} */
  vatNumberValidField = null;

  connectedCallback() {
    if (this.#initialized) return;

    this.form = this.querySelector('#ValidateVATForm');
    this.vatInput = this.querySelector('#vat_reg_no');
    this.emailInput = this.querySelector('#vat_email_address');
    this.validateButton = this.querySelector('#validateVAT-btn');
    this.successMessage = this.querySelector('.cart-vat__message--success');
    this.invalidMessage = this.querySelector('.cart-vat__message--invalid');
    this.feedbackMessage = this.querySelector('.cart-vat__message--feedback');
    this.spinnerIcons = this.querySelectorAll('.cart-vat__spinner');
    this.checkIcons = this.querySelectorAll('.cart-vat__check');
    this.vatValidatedField = this.querySelector('#vat_validated');
    this.vatTimestampField = this.querySelector('#vat_validation_timestamp');
    this.vatNumberValidField = this.querySelector('#vat_no_valid');

    if (!this.form || !this.vatInput || !this.validateButton) {
      return;
    }

    this.#initialized = true;
    this.#bindEvents();
    this.#updateValidateButtonState(this.#getInputValue(this.vatInput));
  }

  get vatValidationUrl() {
    return window.routes?.vat_validation_url || '/apps/dd/vat';
  }

  #bindEvents() {
    this.form?.addEventListener('submit', (event) => this.#onSubmit(event));
    this.vatInput?.addEventListener('input', () => this.#onVatInputChange());
    this.vatInput?.addEventListener('blur', () => this.#onVatInputBlur());
  }

  #loadMessages() {
    if (this.#messages) return;

    this.#messages = {};
    const script = this.querySelector('script[data-vat-messages]');

    if (!script?.textContent) return;

    try {
      const parsed = JSON.parse(script.textContent);

      if (parsed && typeof parsed === 'object') {
        this.#messages = parsed;
      }
    } catch {
      // Fall back to defaults in #getMessage.
    }
  }

  /**
   * @param {string} key
   * @param {string} fallback
   * @returns {string}
   */
  #getMessage(key, fallback) {
    this.#loadMessages();

    const value = this.#messages?.[key];
    return typeof value === 'string' && value ? value : fallback;
  }

  /**
   * @returns {string}
   */
  #getDefaultInvalidMessage() {
    return (
      this.invalidMessage?.textContent?.trim() ||
      this.#getMessage('invalidFormat', '')
    );
  }

  /**
   * @param {Element | null | undefined} input
   * @returns {string}
   */
  #getInputValue(input) {
    return input instanceof HTMLInputElement ? input.value.trim() : '';
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #normalizeVatNumber(value) {
    return value.trim().replace(/\s+/g, '').toUpperCase();
  }

  /**
   * @param {Element | null | undefined} element
   * @param {boolean} hidden
   */
  #setHidden(element, hidden) {
    element?.classList.toggle('hidden', hidden);
  }

  /**
   * @param {Element | null | undefined} input
   * @param {string} value
   */
  #setInputValue(input, value) {
    if (input instanceof HTMLInputElement) {
      input.value = value;
    }
  }

  /**
   * @param {Element | null | undefined} button
   * @param {boolean} disabled
   */
  #setButtonDisabled(button, disabled) {
    if (button instanceof HTMLButtonElement) {
      button.disabled = disabled;
    }
  }

  /**
   * @param {boolean} hasError
   */
  #setInputErrorState(hasError) {
    this.vatInput?.classList.toggle('cart-vat__input--error', hasError);
    this.emailInput?.classList.toggle('cart-vat__input--error', hasError);
  }

  #onVatInputChange() {
    this.#setInputErrorState(false);
    this.#updateValidateButtonState(this.#getInputValue(this.vatInput));
  }

  #onVatInputBlur() {
    const vatNumber = this.#normalizeVatNumber(this.#getInputValue(this.vatInput));

    if (!vatNumber) {
      this.#resetValidationIndicators();
      this.#hideAllMessages();
      return;
    }

    const lastValidatedVat = this.#normalizeVatNumber(
      this.#getInputValue(this.vatNumberValidField)
    );
    const isValidatedMatch = Boolean(lastValidatedVat && lastValidatedVat === vatNumber);

    if (!isValidatedMatch) {
      this.#resetValidationIndicators();
      this.#hideAllMessages();
    }

    this.#updateValidateButtonState(vatNumber);
  }

  /**
   * @param {Event} event
   */
  async #onSubmit(event) {
    event.preventDefault();

    if (this.#isValidating || !this.vatInput) {
      return;
    }

    const vatNumber = this.#normalizeVatNumber(this.#getInputValue(this.vatInput));
    const email = this.#getInputValue(this.emailInput);

    if (!vatNumber) {
      this.#showFeedback(this.#getMessage('empty', ''), true);
      return;
    }

    if (!email) {
      this.#showFeedback(this.#getMessage('emailRequired', ''), true);
      return;
    }

    if (!this.#isValidEmail(email)) {
      this.#showFeedback(this.#getMessage('emailInvalid', ''), true);
      return;
    }

    this.#isValidating = true;
    this.#setButtonDisabled(this.validateButton, true);
    this.#showSpinner();

    try {
      const result = await this.#validateVatNumber(vatNumber, email);

      if (result.valid) {
        await this.#updateCartAttributes(vatNumber, true, email);
        this.#showSuccess();
        return;
      }

      await this.#updateCartAttributes(vatNumber, false);
      this.#showValidationFailure(result.errorMessage);
    } catch {
      await this.#updateCartAttributes(vatNumber, false);
      this.#showFeedback(this.#getMessage('network', ''), true);
    } finally {
      this.#isValidating = false;
      this.#hideSpinner();
      this.#updateValidateButtonState(vatNumber);
    }
  }

  /**
   * @param {string} vatNumber
   * @param {string} email
   * @returns {Promise<VatValidationResult>}
   */
  async #validateVatNumber(vatNumber, email) {
    try {
      const apiUrl = `${this.vatValidationUrl}?vat_number=${encodeURIComponent(
        vatNumber
      )}&email=${encodeURIComponent(email)}`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      });

      let data;
      try {
        data = /** @type {VatValidationResponse | undefined} */ (await response.json());
      } catch {
        data = undefined;
      }

      if (!response.ok) {
        return {
          valid: false,
          errorMessage: this.#getApiErrorMessage(response, data),
        };
      }

      if (data?.valid === true || data?.isValid === true) {
        return { valid: true };
      }

      const apiError = this.#formatApiError(this.#extractApiError(data));

      return {
        valid: false,
        errorMessage: apiError || this.#getDefaultInvalidMessage(),
      };
    } catch {
      return {
        valid: false,
        errorMessage: this.#getMessage('network', ''),
      };
    }
  }

  /**
   * @param {Response} response
   * @param {VatValidationResponse | undefined} data
   * @returns {string}
   */
  #getApiErrorMessage(response, data) {
    const apiError = this.#formatApiError(this.#extractApiError(data));

    if (apiError) {
      return apiError;
    }

    return this.#getStatusFallbackMessage(response.status);
  }

  /**
   * @param {VatValidationResponse | undefined} data
   * @returns {string}
   */
  #extractApiError(data) {
    if (!data || typeof data !== 'object') {
      return '';
    }

    const candidates = [data.error, data.message, data.error_message];

    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate.trim();
      }
    }

    return '';
  }

  /**
   * @param {string} apiError
   * @returns {string}
   */
  #formatApiError(apiError) {
    if (apiError.includes('MS_MAX_CONCURRENT_REQ')) {
      return this.#getMessage('maxConcurrent', '');
    }

    return apiError;
  }

  /**
   * @param {number} status
   * @returns {string}
   */
  #getStatusFallbackMessage(status) {
    if (status === 429) {
      return this.#getMessage('rateLimit', '');
    }

    if (status === 422 || status === 400) {
      return this.#getMessage('invalidFormat', '');
    }

    if (status === 404 || status >= 500) {
      return this.#getMessage('serviceUnavailable', '');
    }

    return this.#getMessage('unexpected', '');
  }

  /**
   * @param {string} email
   * @returns {boolean}
   */
  #isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  /**
   * @param {string} vatNumber
   * @param {boolean} isValid
   * @param {string} [email]
   */
  async #updateCartAttributes(vatNumber, isValid, email = '') {
    const timestamp = new Date().toISOString();
    const formData = new FormData();

    if (isValid) {
      formData.append('attributes[VAT number]', vatNumber);
      formData.append('attributes[VAT email]', email);
      formData.append('attributes[VAT Number Valid]', vatNumber);
      formData.append('attributes[vat_validated]', 'true');
      formData.append('attributes[vat_validation_timestamp]', timestamp);
    } else {
      formData.append('attributes[VAT number]', '');
      formData.append('attributes[VAT email]', '');
      formData.append('attributes[VAT Number Valid]', '');
      formData.append('attributes[vat_validated]', 'false');
      formData.append('attributes[vat_validation_timestamp]', '');
    }

    const response = await fetch('/cart/update.js', {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Failed to update cart attributes');
    }

    this.#setInputValue(this.vatValidatedField, isValid ? 'true' : 'false');

    if (isValid) {
      this.#setInputValue(this.vatTimestampField, timestamp);
      this.#setInputValue(this.vatNumberValidField, vatNumber);
      return;
    }

    this.#setInputValue(this.vatTimestampField, '');
    this.#setInputValue(this.vatNumberValidField, '');
  }

  #showSuccess() {
    this.#hideAllMessages();
    this.#setInputErrorState(false);
    this.#setHidden(this.successMessage, false);

    for (const icon of this.checkIcons ?? []) {
      this.#setHidden(icon, false);
    }
  }

  /**
   * @param {string | undefined} errorMessage
   */
  #showValidationFailure(errorMessage) {
    this.#hideAllMessages();
    this.#resetValidationIndicators();

    if (errorMessage && errorMessage !== this.#getDefaultInvalidMessage()) {
      this.#showFeedback(errorMessage, true);
      return;
    }

    this.#setHidden(this.invalidMessage, false);
    this.#setInputErrorState(true);
  }

  /**
   * @param {string} message
   * @param {boolean} [markInputsInvalid]
   */
  #showFeedback(message, markInputsInvalid = false) {
    this.#hideAllMessages();
    this.#resetValidationIndicators();
    this.#setInputErrorState(markInputsInvalid);

    if (!this.feedbackMessage) {
      return;
    }

    this.feedbackMessage.textContent = message;
    this.#setHidden(this.feedbackMessage, false);
  }

  #hideAllMessages() {
    this.#setHidden(this.successMessage, true);
    this.#setHidden(this.invalidMessage, true);
    this.#setHidden(this.feedbackMessage, true);

    if (this.feedbackMessage) {
      this.feedbackMessage.textContent = '';
    }
  }

  #resetValidationIndicators() {
    for (const icon of this.checkIcons ?? []) {
      this.#setHidden(icon, true);
    }
  }

  /**
   * @param {string} value
   */
  #updateValidateButtonState(value) {
    const vatNumber = this.#normalizeVatNumber(value || '');
    const shouldDisable = !vatNumber || this.#isValidating;
    this.#setButtonDisabled(this.validateButton, shouldDisable);
  }

  #showSpinner() {
    for (const icon of this.spinnerIcons ?? []) {
      this.#setHidden(icon, false);
    }

    this.#resetValidationIndicators();
    this.#hideAllMessages();
  }

  #hideSpinner() {
    for (const icon of this.spinnerIcons ?? []) {
      this.#setHidden(icon, true);
    }
  }
}

if (!customElements.get('vat-validator')) {
  customElements.define('vat-validator', VatValidator);
}
